"use client";

import {
  Activity,
  AlertTriangle,
  BarChart3,
  Check,
  ChevronDown,
  CircleGauge,
  Eye,
  EyeOff,
  FileVideo,
  Info,
  Pause,
  Play,
  PlayCircle,
  RotateCcw,
  Ruler,
  Save,
  ScanLine,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  UploadCloud,
  Video,
  Waves,
} from "lucide-react";
import type { Options as PoseOptions, Results } from "@mediapipe/pose";
import {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  analyzeSamples,
  landmarkMeasurements,
  type AnalysisMetrics,
  type AnalysisResult,
  type CameraView,
  type Phase,
  type PhaseScore,
  type PracticeItem,
  type PoseSample,
  type RawPoseSample,
  type RowingMode,
  type TechniqueFinding,
} from "./rowing-analysis";

type AnalysisStage = "idle" | "ready" | "analyzing" | "complete" | "error";
type SweepSide = "stroke" | "bow";

type SavedSession = {
  id: string;
  savedAt: string;
  mode: RowingMode;
  boatClass: string;
  sweepSide: SweepSide | null;
  cameraView: CameraView;
  strokeRate: number | null;
  overallScore: number | null;
  confidence: number;
  consistency: number | null;
  phaseScores: Record<Phase, number | null>;
};

type PoseRuntime = {
  close(): Promise<void>;
  initialize(): Promise<void>;
  onResults(listener: (results: Results) => void): void;
  send(inputs: { image: HTMLVideoElement }): Promise<void>;
  setOptions(options: PoseOptions): void;
};

declare global {
  interface Window {
    Pose?: new (config: { locateFile: (file: string) => string }) => PoseRuntime;
  }
}

const STORAGE_KEY = "strokescore-local-sessions-v2";

const demoMetrics: AnalysisMetrics = {
  strokeRate: 31,
  driveRecovery: 2.1,
  sequence: 87,
  consistency: 91,
  confidence: 92,
  confidenceLabel: "High",
  catchKnee: 54,
  finishKnee: 164,
  trunkSwing: 31,
  elbowRange: 118,
  analyzedSeconds: 16.8,
  strokesObserved: 8,
  periodicity: 0.88,
  overallScore: 84,
  validFramePercent: 94,
};

const demoPhaseScores: PhaseScore[] = [
  { phase: "Catch", score: 78, confidence: 90, label: "On track", summary: "Preparation, compression repeatability, and control into the front end." },
  { phase: "Drive", score: 88, confidence: 92, label: "Strong", summary: "Leg–trunk–arm timing and early handle connection." },
  { phase: "Finish", score: 82, confidence: 89, label: "On track", summary: "Leg-drive completion and repeatability at the back end." },
  { phase: "Recovery", score: 74, confidence: 91, label: "On track", summary: "Hands–body–slide order, rhythm, and approach control." },
];

const demoFindings: TechniqueFinding[] = [
  {
    id: "demo-recovery",
    title: "Rushed approach to the catch",
    description: "A larger share of the slide compression occurs near the end of the recovery.",
    evidence: "Detected in 5 of 8 sample strokes with high landmark confidence.",
    cue: "Stay patient into the front end.",
    drill: "Use a light body-over pause, then glide through the final quarter of the slide.",
    frequency: 0.63,
    confidence: 89,
    seekTime: 8.4,
    tone: "watch",
  },
];

const demoPractice: PracticeItem[] = [
  {
    title: "Smooth the approach to the catch",
    cue: "Stay patient into the front end.",
    drill: "Use a light body-over pause, then glide through the final quarter of the slide.",
    check: "Re-record 6–10 strokes from the same angle and compare recovery control.",
    seekTime: 8.4,
  },
];

const cameraOptions: Array<{ value: CameraView; label: string; note: string }> = [
  { value: "side", label: "Fixed, side-on", note: "Required for phase scores, joint angles, and technique findings." },
  { value: "diagonal", label: "Fixed, diagonal", note: "Rhythm only. Perspective makes joint-angle scoring unreliable." },
  { value: "launch", label: "Moving coach launch", note: "Rhythm only. Camera motion lowers measurement confidence." },
  { value: "bow", label: "Bow or stern view", note: "This version cannot score sagittal body sequencing from this view." },
];

const boatOptions: Record<RowingMode, string[]> = {
  sculling: ["1x", "2x", "4x"],
  sweep: ["2- / 2+", "4- / 4+", "8+"],
};

const phaseColors: Record<Phase, string> = {
  Catch: "#17395a",
  Drive: "#13d8f3",
  Finish: "#147b83",
  Recovery: "#294765",
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const formatTime = (seconds: number) => {
  const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${String(minutes).padStart(2, "0")}:${remainder.toFixed(2).padStart(5, "0")}`;
};

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(iso));

const waitForSeek = (video: HTMLVideoElement, time: number) =>
  new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("Video frame could not be decoded."));
    }, 5000);
    const cleanup = () => {
      window.clearTimeout(timeout);
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("error", onError);
    };
    const onSeeked = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error("This video codec is not supported by the browser."));
    };
    video.addEventListener("seeked", onSeeked, { once: true });
    video.addEventListener("error", onError, { once: true });
    video.currentTime = Math.min(Math.max(0, time), Math.max(0, video.duration - 0.01));
  });

const loadPoseRuntime = () =>
  new Promise<NonNullable<Window["Pose"]>>((resolve, reject) => {
    if (window.Pose) {
      resolve(window.Pose);
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>("script[data-mediapipe-pose]");
    if (existing) {
      existing.addEventListener("load", () => window.Pose
        ? resolve(window.Pose)
        : reject(new Error("Pose model did not initialize.")), { once: true });
      existing.addEventListener("error", () => reject(new Error("Pose model files could not be loaded.")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = "/mediapipe/pose.js";
    script.async = true;
    script.dataset.mediapipePose = "true";
    script.onload = () => window.Pose
      ? resolve(window.Pose)
      : reject(new Error("Pose model did not initialize."));
    script.onerror = () => reject(new Error("Pose model files could not be loaded."));
    document.head.appendChild(script);
  });

function phaseScoreRecord(scores: PhaseScore[]) {
  return Object.fromEntries(scores.map((score) => [score.phase, score.score])) as Record<Phase, number | null>;
}

export default function Home() {
  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const poseRef = useRef<PoseRuntime | null>(null);
  const frameRef = useRef<number | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const samplesRef = useRef<PoseSample[]>([]);
  const [mode, setMode] = useState<RowingMode>("sculling");
  const [boatClass, setBoatClass] = useState("1x");
  const [sweepSide, setSweepSide] = useState<SweepSide>("stroke");
  const [cameraView, setCameraView] = useState<CameraView>("side");
  const [stage, setStage] = useState<AnalysisStage>("idle");
  const [fileName, setFileName] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [videoDuration, setVideoDuration] = useState(22.8);
  const [videoAspect, setVideoAspect] = useState(16 / 9);
  const [currentTime, setCurrentTime] = useState(7.34);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("Ready for video");
  const [errorMessage, setErrorMessage] = useState("");
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [showAngles, setShowAngles] = useState(true);
  const [showTrails, setShowTrails] = useState(false);
  const [selectedStrokeId, setSelectedStrokeId] = useState<number | null>(null);
  const [history, setHistory] = useState<SavedSession[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) as SavedSession[] : [];
    } catch {
      return [];
    }
  });
  const [savedCurrent, setSavedCurrent] = useState(false);

  const isMeasured = stage === "complete" && Boolean(analysis);
  const displayMetrics = isMeasured ? analysis!.metrics : demoMetrics;
  const displayPhaseScores = isMeasured ? analysis!.phaseScores : demoPhaseScores;
  const displayFindings = isMeasured ? analysis!.findings : demoFindings;
  const displayPractice = isMeasured ? analysis!.practicePlan : demoPractice;

  const activeSample = useMemo(() => {
    if (!analysis?.samples.length) return null;
    return analysis.samples.reduce((closest, sample) =>
      Math.abs(sample.time - currentTime) < Math.abs(closest.time - currentTime)
        ? sample
        : closest,
    );
  }, [analysis, currentTime]);

  const activePhase: Phase = activeSample?.phase ?? "Drive";
  const activeCycle = useMemo(() => {
    if (!analysis?.cycles.length) return null;
    if (activeSample?.strokeId) {
      return analysis.cycles.find((cycle) => cycle.id === activeSample.strokeId) ?? analysis.cycles[0];
    }
    return analysis.cycles.find((cycle) => cycle.id === selectedStrokeId) ?? analysis.cycles[0];
  }, [analysis, activeSample, selectedStrokeId]);

  const matchedPrevious = useMemo(() => {
    if (!isMeasured) return null;
    return history.find((session) =>
      session.mode === mode
      && session.cameraView === cameraView
      && session.boatClass === boatClass
      && (
        session.strokeRate === null
        || displayMetrics.strokeRate === null
        || Math.abs(session.strokeRate - displayMetrics.strokeRate) <= 3
      ),
    ) ?? null;
  }, [history, isMeasured, mode, cameraView, boatClass, displayMetrics.strokeRate]);

  const drawPose = useCallback((sample: PoseSample | null) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }
    const context = canvas.getContext("2d");
    if (!context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    if (!sample) return;
    const indices = sample.side === "left"
      ? [11, 13, 15, 23, 25, 27]
      : [12, 14, 16, 24, 26, 28];
    const connections = [[0, 1], [1, 2], [0, 3], [3, 4], [4, 5]];

    if (showTrails) {
      const nearby = samplesRef.current.filter((candidate) =>
        candidate.time <= sample.time
        && candidate.time >= sample.time - 0.7
        && candidate.side === sample.side,
      );
      [indices[2], indices[3]].forEach((landmarkIndex, trailIndex) => {
        context.beginPath();
        nearby.forEach((candidate, index) => {
          const point = candidate.landmarks[landmarkIndex];
          if (!point || (point.visibility ?? 0) < 0.45) return;
          const x = point.x * canvas.width;
          const y = point.y * canvas.height;
          if (index === 0) context.moveTo(x, y);
          else context.lineTo(x, y);
        });
        context.strokeStyle = trailIndex === 0 ? "rgba(20,217,245,.42)" : "rgba(114,230,166,.35)";
        context.lineWidth = Math.max(2, canvas.width / 650);
        context.stroke();
      });
    }

    if (showSkeleton) {
      context.lineCap = "round";
      context.lineJoin = "round";
      context.shadowColor = "rgba(20, 217, 245, .72)";
      context.shadowBlur = 12;
      context.lineWidth = Math.max(3, canvas.width / 430);
      connections.forEach(([from, to]) => {
        const start = sample.landmarks[indices[from]];
        const end = sample.landmarks[indices[to]];
        if (!start || !end) return;
        const reliable = (start.visibility ?? 0) >= 0.45 && (end.visibility ?? 0) >= 0.45;
        context.strokeStyle = reliable ? "#14d9f5" : "rgba(145,164,180,.42)";
        context.beginPath();
        context.moveTo(start.x * canvas.width, start.y * canvas.height);
        context.lineTo(end.x * canvas.width, end.y * canvas.height);
        context.stroke();
      });
      indices.forEach((index) => {
        const point = sample.landmarks[index];
        if (!point) return;
        const reliable = (point.visibility ?? 0) >= 0.45;
        context.beginPath();
        context.fillStyle = reliable ? "#14d9f5" : "#617483";
        context.arc(
          point.x * canvas.width,
          point.y * canvas.height,
          Math.max(5, canvas.width / 180),
          0,
          Math.PI * 2,
        );
        context.fill();
        context.shadowBlur = 0;
        context.strokeStyle = reliable ? "#eafcff" : "#9aabb7";
        context.lineWidth = Math.max(2, canvas.width / 700);
        context.stroke();
        context.shadowBlur = 12;
      });
    }

    if (showAngles) {
      const drawLabel = (landmarkIndex: number, label: string, reliable: boolean) => {
        const point = sample.landmarks[landmarkIndex];
        if (!point) return;
        const x = point.x * canvas.width + 12;
        const y = point.y * canvas.height - 12;
        context.shadowBlur = 0;
        context.font = `600 ${Math.max(12, canvas.width / 72)}px Geist, sans-serif`;
        const width = context.measureText(label).width + 16;
        context.fillStyle = "rgba(5,15,23,.84)";
        context.strokeStyle = reliable ? "rgba(20,217,245,.72)" : "rgba(145,164,180,.5)";
        context.lineWidth = 1;
        context.beginPath();
        context.roundRect(x, y - 20, width, 27, 7);
        context.fill();
        context.stroke();
        context.fillStyle = reliable ? "#eafcff" : "#9aabb7";
        context.fillText(label, x + 8, y - 2);
      };
      drawLabel(indices[4], `${Math.round(sample.kneeAngle)}° knee`, sample.jointConfidence.knee >= 0.55);
      drawLabel(indices[1], `${Math.round(sample.elbowAngle)}° elbow`, sample.jointConfidence.elbow >= 0.55);
      drawLabel(indices[0], `${Math.round(sample.trunkAngle)}° trunk`, sample.jointConfidence.trunk >= 0.55);
    }
  }, [showAngles, showSkeleton, showTrails]);

  useEffect(() => {
    samplesRef.current = analysis?.samples ?? [];
    drawPose(activeSample);
  }, [analysis, activeSample, drawPose]);

  useEffect(() => () => {
    if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    void poseRef.current?.close();
  }, []);

  const changeMode = (nextMode: RowingMode) => {
    setMode(nextMode);
    setBoatClass(boatOptions[nextMode][0]);
  };

  const setFile = (file: File) => {
    if (!file.type.startsWith("video/")) {
      setErrorMessage("Choose a video file such as MP4, MOV, or WebM.");
      setStage("error");
      return;
    }
    if (file.size > 350 * 1024 * 1024) {
      setErrorMessage("Keep the clip under 350 MB so it can be analyzed reliably in your browser.");
      setStage("error");
      return;
    }
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setVideoUrl(url);
    setFileName(file.name);
    setStage("ready");
    setAnalysis(null);
    setProgress(0);
    setStatusText("Video loaded");
    setErrorMessage("");
    setCurrentTime(0);
    setSavedCurrent(false);
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) setFile(file);
    event.target.value = "";
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) setFile(file);
  };

  const initializePose = async () => {
    if (poseRef.current) return poseRef.current;
    const PoseConstructor = await loadPoseRuntime();
    const pose = new PoseConstructor({ locateFile: (file) => `/mediapipe/${file}` });
    pose.setOptions({
      modelComplexity: 1,
      smoothLandmarks: true,
      enableSegmentation: false,
      minDetectionConfidence: 0.6,
      minTrackingConfidence: 0.6,
    });
    await pose.initialize();
    poseRef.current = pose;
    return pose;
  };

  const runAnalysis = async () => {
    const video = videoRef.current;
    if (!video || !videoUrl || !Number.isFinite(video.duration)) return;
    if (video.duration < 7.5) {
      setErrorMessage("Use at least 8–12 seconds of continuous rowing so three complete strokes can be verified.");
      setStage("error");
      return;
    }
    setStage("analyzing");
    setProgress(2);
    setErrorMessage("");
    setStatusText("Loading the full pose model");
    setIsPlaying(false);
    video.pause();

    try {
      const pose = await initializePose();
      const captured: RawPoseSample[] = [];
      let latestResults: Results | null = null;
      pose.onResults((results) => { latestResults = results; });
      const start = Math.min(0.2, video.duration * 0.015);
      const maximumAnalysisDuration = 90;
      const end = Math.min(video.duration - 0.05, start + maximumAnalysisDuration);
      const analysisDuration = Math.max(0.1, end - start);
      const targetSampleRate = cameraView === "side" ? 10 : 8;
      const maximumFrameCount = 720;
      const sampleRate = Math.min(targetSampleRate, maximumFrameCount / analysisDuration);
      const frameCount = Math.max(40, Math.floor((end - start) * sampleRate));
      setStatusText(
        `Tracking 33 landmarks across ${Math.round(analysisDuration)} seconds at ${sampleRate.toFixed(1)} samples per second`,
      );

      for (let index = 0; index <= frameCount; index += 1) {
        const time = start + ((end - start) * index) / frameCount;
        await waitForSeek(video, time);
        latestResults = null;
        await pose.send({ image: video });
        const current = latestResults as Results | null;
        if (current?.poseLandmarks?.length) {
          const measurement = landmarkMeasurements(current.poseLandmarks);
          if (measurement && measurement.visibility >= 0.36) {
            captured.push({
              time,
              landmarks: current.poseLandmarks.map((landmark) => ({ ...landmark })),
              ...measurement,
            });
          }
        }
        setProgress(Math.round(7 + (index / frameCount) * 80));
        if (index === Math.floor(frameCount * 0.38)) {
          setStatusText("Checking stroke periodicity and landmark quality");
        }
        if (index === Math.floor(frameCount * 0.72)) {
          setStatusText("Locating catch, drive, finish, and recovery events");
        }
      }

      if (captured.length < Math.max(35, frameCount * 0.52)) {
        throw new Error(
          "The rower was not visible reliably enough across the clip. Use a fixed camera, brighter footage, and keep the full athlete in frame.",
        );
      }

      setStatusText("Scoring each stroke and verifying repeated findings");
      setProgress(91);
      const result = analyzeSamples(captured, cameraView, frameCount + 1);
      setAnalysis(result);
      setProgress(100);
      setStage("complete");
      const durationNote = end < video.duration - 0.1
        ? ` in the first ${Math.round(analysisDuration)} seconds`
        : "";
      setStatusText(`${result.metrics.strokesObserved} complete strokes verified${durationNote} on this device`);
      setSelectedStrokeId(result.cycles[0]?.id ?? null);
      const firstTime = result.cycles[0]?.catchTime ?? result.samples[0].time;
      await waitForSeek(video, firstTime);
      setCurrentTime(firstTime);
      drawPose(result.samples.reduce((closest, sample) =>
        Math.abs(sample.time - firstTime) < Math.abs(closest.time - firstTime)
          ? sample
          : closest,
      ));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "The analysis could not be completed.");
      setStage("error");
      setStatusText("Analysis stopped");
      setProgress(0);
    }
  };

  const startPlaybackLoop = () => {
    const tick = () => {
      const video = videoRef.current;
      if (!video) return;
      setCurrentTime(video.currentTime);
      if (samplesRef.current.length) {
        const closest = samplesRef.current.reduce((best, sample) =>
          Math.abs(sample.time - video.currentTime) < Math.abs(best.time - video.currentTime)
            ? sample
            : best,
        );
        drawPose(closest);
        if (closest.strokeId) setSelectedStrokeId(closest.strokeId);
      }
      if (!video.paused && !video.ended) {
        frameRef.current = window.requestAnimationFrame(tick);
      }
    };
    frameRef.current = window.requestAnimationFrame(tick);
  };

  const togglePlayback = async () => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;
    if (video.paused) {
      await video.play();
      setIsPlaying(true);
      startPlaybackLoop();
    } else {
      video.pause();
      setIsPlaying(false);
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
    }
  };

  const seekTo = (time: number) => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;
    video.pause();
    setIsPlaying(false);
    video.currentTime = clamp(time, 0, video.duration || 0);
    setCurrentTime(video.currentTime);
    const closest = samplesRef.current.length
      ? samplesRef.current.reduce((best, sample) =>
        Math.abs(sample.time - video.currentTime) < Math.abs(best.time - video.currentTime)
          ? sample
          : best,
      )
      : null;
    drawPose(closest);
    if (closest?.strokeId) setSelectedStrokeId(closest.strokeId);
  };

  const seekPhase = (phase: Phase) => {
    if (!activeCycle) return;
    const time = phase === "Catch"
      ? activeCycle.catchTime
      : phase === "Drive"
        ? (activeCycle.catchTime + activeCycle.finishTime) / 2
        : phase === "Finish"
          ? activeCycle.finishTime
          : (activeCycle.finishTime + activeCycle.nextCatchTime) / 2;
    seekTo(time);
  };

  const seekStroke = (strokeId: number) => {
    const cycle = analysis?.cycles.find((candidate) => candidate.id === strokeId);
    if (!cycle) return;
    setSelectedStrokeId(strokeId);
    seekTo(cycle.catchTime);
  };

  const resetAnalysis = () => {
    setAnalysis(null);
    setProgress(0);
    setStage(videoUrl ? "ready" : "idle");
    setStatusText(videoUrl ? "Video loaded" : "Ready for video");
    setSelectedStrokeId(null);
    setSavedCurrent(false);
    const canvas = canvasRef.current;
    canvas?.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveReport = () => {
    if (!analysis) return;
    const session: SavedSession = {
      id: `${Date.now()}`,
      savedAt: new Date().toISOString(),
      mode,
      boatClass,
      sweepSide: mode === "sweep" ? sweepSide : null,
      cameraView,
      strokeRate: analysis.metrics.strokeRate,
      overallScore: analysis.metrics.overallScore,
      confidence: analysis.metrics.confidence,
      consistency: analysis.metrics.consistency,
      phaseScores: phaseScoreRecord(analysis.phaseScores),
    };
    const next = [session, ...history].slice(0, 20);
    setHistory(next);
    setSavedCurrent(true);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const currentSampleLabel = isMeasured
    ? `Stroke ${activeSample?.strokeId ?? selectedStrokeId ?? 1} · ${activePhase}`
    : "Demo workspace · Drive";

  return (
    <main className="app-shell">
      <header className="topbar">
        <a className="brand" href="#analyze" aria-label="StrokeScore AI home">
          <span className="brand-mark"><Waves size={24} strokeWidth={2.6} /></span>
          <span>StrokeScore <em>AI</em></span>
        </a>
        <nav aria-label="Primary navigation">
          <a className="active" href="#analyze">Analyze</a>
          <a href="#phases">Phase scores</a>
          <a href="#coaching">Coaching</a>
          <a href="#progress">Progress</a>
        </nav>
        <div className="model-status" title="Pose analysis runs locally in your browser">
          <span className="status-dot" /> On-device AI
        </div>
      </header>

      <section className="hero" id="analyze">
        <div className="hero-copy">
          <p className="eyebrow"><ScanLine size={15} /> AI rowing biomechanics</p>
          <h1>See every phase. Understand every stroke.</h1>
          <p className="hero-subtitle">
            Upload an on-water clip for stroke detection, phase scoring, explainable technique findings, and a practice plan—without markers or wearables.
          </p>
        </div>
        <div className="hero-actions">
          <input ref={inputRef} className="sr-only" type="file" accept="video/mp4,video/quicktime,video/webm,video/*" onChange={onFileChange} />
          <button className="upload-button" type="button" onClick={() => inputRef.current?.click()}>
            <UploadCloud size={22} />
            {fileName ? "Choose another video" : "Upload rowing video"}
          </button>
          <div className="mode-switch" aria-label="Rowing discipline">
            <button type="button" className={mode === "sculling" ? "selected" : ""} onClick={() => changeMode("sculling")} aria-pressed={mode === "sculling"}>
              <span className="crossed-oars">×</span> Sculling
            </button>
            <button type="button" className={mode === "sweep" ? "selected" : ""} onClick={() => changeMode("sweep")} aria-pressed={mode === "sweep"}>
              <span className="single-oar">╱</span> Sweep
            </button>
          </div>
        </div>
      </section>

      <section
        className={`analysis-panel ${isDragging ? "dragging" : ""}`}
        onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        aria-label="Rowing video analysis workspace"
      >
        {isDragging && <div className="drop-overlay"><UploadCloud size={42} /><strong>Drop your rowing clip here</strong></div>}
        <div className="viewer-column">
          <div className="viewer" style={videoUrl ? { aspectRatio: videoAspect } : undefined}>
            {videoUrl ? (
              <>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  playsInline
                  preload="metadata"
                  onLoadedMetadata={(event) => {
                    const video = event.currentTarget;
                    setVideoDuration(video.duration);
                    setVideoAspect(video.videoWidth / Math.max(1, video.videoHeight));
                    setCurrentTime(0);
                  }}
                  onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
                  onEnded={() => setIsPlaying(false)}
                />
                <canvas ref={canvasRef} className="pose-canvas" aria-hidden="true" />
              </>
            ) : <div className="demo-image" role="img" aria-label="Side-view single sculler prepared for pose analysis" />}
            <div className="viewer-shade" />
            <span className="stroke-pill">{currentSampleLabel}</span>
            <span className={`confidence-pill confidence-${displayMetrics.confidenceLabel.toLowerCase()}`}>
              <span /> Confidence {displayMetrics.confidenceLabel}
            </span>
            {isMeasured && (
              <div className="overlay-controls" aria-label="Video overlays">
                <button type="button" className={showSkeleton ? "selected" : ""} onClick={() => setShowSkeleton((value) => !value)} aria-pressed={showSkeleton}>
                  {showSkeleton ? <Eye size={14} /> : <EyeOff size={14} />} Pose
                </button>
                <button type="button" className={showAngles ? "selected" : ""} onClick={() => setShowAngles((value) => !value)} aria-pressed={showAngles}>
                  <Ruler size={14} /> Angles
                </button>
                <button type="button" className={showTrails ? "selected" : ""} onClick={() => setShowTrails((value) => !value)} aria-pressed={showTrails}>
                  <Activity size={14} /> Trails
                </button>
              </div>
            )}
            {!videoUrl && (
              <div className="demo-skeleton" aria-hidden="true">
                <span className="joint joint-1" /><span className="joint joint-2" /><span className="joint joint-3" />
                <span className="joint joint-4" /><span className="joint joint-5" /><span className="joint joint-6" />
                <i className="bone bone-1" /><i className="bone bone-2" /><i className="bone bone-3" /><i className="bone bone-4" /><i className="bone bone-5" />
              </div>
            )}
            {stage === "idle" && (
              <button className="empty-prompt" type="button" onClick={() => inputRef.current?.click()}>
                <span><FileVideo size={26} /></span><strong>Analyze your own stroke</strong><small>Use 8–90 seconds of continuous rowing</small>
              </button>
            )}
            {stage === "ready" && (
              <div className="ready-card">
                <div><FileVideo size={24} /><span><strong>{fileName}</strong><small>{formatTime(videoDuration)} loaded locally</small></span></div>
                <div className="setup-grid">
                  <label>
                    <span>Boat</span>
                    <div className="select-wrap">
                      <select value={boatClass} onChange={(event) => setBoatClass(event.target.value)}>
                        {boatOptions[mode].map((option) => <option value={option} key={option}>{option}</option>)}
                      </select><ChevronDown size={16} />
                    </div>
                  </label>
                  {mode === "sweep" && (
                    <label>
                      <span>Rowing side</span>
                      <div className="select-wrap">
                        <select value={sweepSide} onChange={(event) => setSweepSide(event.target.value as SweepSide)}>
                          <option value="stroke">Stroke side</option><option value="bow">Bow side</option>
                        </select><ChevronDown size={16} />
                      </div>
                    </label>
                  )}
                  <label className="view-field">
                    <span>Recording view</span>
                    <div className="select-wrap">
                      <select value={cameraView} onChange={(event) => setCameraView(event.target.value as CameraView)}>
                        {cameraOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
                      </select><ChevronDown size={16} />
                    </div>
                  </label>
                </div>
                <p className={cameraView === "side" ? "setup-note good" : "setup-note warning"}>
                  {cameraOptions.find((option) => option.value === cameraView)?.note}
                </p>
                <button type="button" onClick={runAnalysis}><Sparkles size={18} /> Run verified analysis</button>
              </div>
            )}
            {stage === "analyzing" && (
              <div className="analyzing-card" aria-live="polite">
                <span className="scan-icon"><ScanLine size={27} /></span><strong>{statusText}</strong>
                <div className="progress-track"><i style={{ width: `${progress}%` }} /></div>
                <small>{progress}% · Video and results stay on this device</small>
              </div>
            )}
            {stage === "error" && (
              <div className="error-card" role="alert">
                <AlertTriangle size={26} /><strong>Analysis needs a clearer clip</strong><p>{errorMessage}</p>
                <div><button type="button" onClick={() => setStage(videoUrl ? "ready" : "idle")}>Try again</button><button type="button" className="secondary" onClick={() => inputRef.current?.click()}>Choose video</button></div>
              </div>
            )}
          </div>

          <div className="timeline-controls">
            <button type="button" className="play-button" onClick={togglePlayback} disabled={!videoUrl || stage === "analyzing"} aria-label={isPlaying ? "Pause video" : "Play video"}>
              {isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
            </button>
            <span className="timecode">{formatTime(currentTime)} <small>/ {formatTime(videoDuration)}</small></span>
            <div className="phase-track" aria-label="Stroke phases">
              {(["Catch", "Drive", "Finish", "Recovery"] as Phase[]).map((phase) => (
                <button type="button" key={phase} className={activePhase === phase ? "active" : ""} style={{ "--phase-color": phaseColors[phase] } as React.CSSProperties} onClick={() => seekPhase(phase)} disabled={!isMeasured} title={phase === "Catch" || phase === "Finish" ? `${phase} event estimate` : phase}>
                  {phase}
                </button>
              ))}
              <i className="playhead" style={{ left: `${clamp((currentTime / Math.max(0.1, videoDuration)) * 100, 0, 100)}%` }} />
            </div>
          </div>
          {isMeasured && analysis && (
            <div className="stroke-strip" aria-label="Detected strokes">
              <span>Detected strokes</span>
              <div>
                {analysis.cycles.map((cycle) => (
                  <button type="button" key={cycle.id} className={cycle.id === (activeSample?.strokeId ?? selectedStrokeId) ? "selected" : ""} onClick={() => seekStroke(cycle.id)}>
                    <strong>{cycle.id}</strong><small>{cycle.confidence}%</small>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="metrics-rail" aria-label="Analysis metrics">
          <div className="rail-heading">
            <div><p>{isMeasured ? "Verified result" : "Sample result"}</p><strong>{isMeasured ? statusText : "Preview the output before uploading"}</strong></div>
            {isMeasured && <button type="button" onClick={resetAnalysis} aria-label="Reset analysis"><RotateCcw size={17} /></button>}
          </div>
          <MetricCard icon={<Target size={26} />} label="StrokeScore" value={displayMetrics.overallScore === null ? "—" : String(displayMetrics.overallScore)} unit={displayMetrics.overallScore === null ? "" : "/100"} accent={displayMetrics.overallScore === null ? "amber" : displayMetrics.overallScore >= 80 ? "green" : "amber"} detail={displayMetrics.overallScore === null ? "A reliable fixed side-on view is required" : "Evidence-weighted experimental movement score; open the phase breakdown for details"} />
          <MetricCard icon={<CircleGauge size={26} />} label="Stroke rate" value={displayMetrics.strokeRate ? String(displayMetrics.strokeRate) : "—"} unit={displayMetrics.strokeRate ? "spm" : ""} accent="cyan" detail={displayMetrics.strokeRate ? "Median rate across complete detected stroke cycles" : "Not enough periodic motion detected"} />
          <MetricCard icon={<BarChart3 size={26} />} label="Consistency" value={displayMetrics.consistency === null ? "—" : String(displayMetrics.consistency)} unit={displayMetrics.consistency === null ? "" : "/100"} accent={displayMetrics.consistency !== null && displayMetrics.consistency >= 80 ? "green" : "amber"} detail="Stroke shapes are phase-normalized and compared with the session median—not inferred from periodicity alone" />
          <MetricCard icon={<ShieldCheck size={27} />} label="Measurement confidence" value={displayMetrics.confidenceLabel} accent={displayMetrics.confidenceLabel === "High" ? "green" : displayMetrics.confidenceLabel === "Medium" ? "amber" : "red"} detail={`${displayMetrics.confidence}% from landmark visibility, valid-frame coverage, camera view, periodicity, and cycle quality`} />
        </aside>
      </section>

      <section className="phase-section" id="phases">
        <SectionHeading eyebrow="Four-part stroke report" title={isMeasured ? "Your phase scores" : "Every phase, explained"} measured={isMeasured} />
        <div className="phase-score-grid">
          {displayPhaseScores.map((phaseScore) => (
            <button type="button" className={`phase-score-card ${phaseScore.label.toLowerCase().replace(" ", "-")}`} key={phaseScore.phase} onClick={() => isMeasured && seekPhase(phaseScore.phase)} disabled={!isMeasured}>
              <span className="phase-name"><i style={{ background: phaseColors[phaseScore.phase] }} />{phaseScore.phase}</span>
              <strong>{phaseScore.score ?? "—"}<small>{phaseScore.score === null ? "" : "/100"}</small></strong>
              <em>{phaseScore.label}</em><p>{phaseScore.summary}</p>
            </button>
          ))}
        </div>
        <div className="measurement-grid advanced">
          <Measurement label="Catch knee estimate" value={displayMetrics.catchKnee === null ? "—" : `${displayMetrics.catchKnee}°`} note="Median at detected catch events" />
          <Measurement label="Leg-drive end" value={displayMetrics.finishKnee === null ? "—" : `${displayMetrics.finishKnee}°`} note="Median knee angle when extension ends" />
          <Measurement label="Trunk swing" value={displayMetrics.trunkSwing === null ? "—" : `${displayMetrics.trunkSwing}°`} note="Median camera-plane change through the drive" />
          <Measurement label="Drive:recovery" value={displayMetrics.driveRecovery === null ? "—" : `1:${displayMetrics.driveRecovery.toFixed(1)}`} note="Median timing across complete strokes" />
        </div>
        {isMeasured && analysis && (
          <div className="limitations-card"><Info size={19} /><div><strong>Read the result correctly</strong>{analysis.limitations.map((limitation) => <p key={limitation}>{limitation}</p>)}</div></div>
        )}
      </section>

      <section className="coaching-section" id="coaching">
        <SectionHeading eyebrow="Explainable coaching" title={isMeasured ? "What StrokeScore noticed" : "Evidence before advice"} measured={isMeasured} />
        <div className="finding-grid">
          {displayFindings.length ? displayFindings.map((finding) => (
            <article className={`finding-card ${finding.tone}`} key={finding.id}>
              <div className="finding-top"><span><AlertTriangle size={18} /></span><div><p>{finding.tone === "focus" ? "Priority" : "Watch"}</p><h3>{finding.title}</h3></div><strong>{Math.round(finding.frequency * 100)}%</strong></div>
              <p>{finding.description}</p><div className="evidence"><ShieldCheck size={16} /><span><strong>{finding.confidence}% confidence</strong>{finding.evidence}</span></div>
              <button type="button" onClick={() => isMeasured && seekTo(finding.seekTime)} disabled={!isMeasured}><PlayCircle size={17} /> Watch evidence</button>
            </article>
          )) : (
            <article className="finding-card clear"><div className="finding-top"><span><Check size={18} /></span><div><p>Clear</p><h3>No repeated high-confidence issue found</h3></div></div><p>StrokeScore did not force a warning from uncertain measurements. Review individual strokes or test again at another rate.</p></article>
          )}
        </div>

        <div className="practice-panel">
          <div className="practice-heading"><p className="eyebrow"><Sparkles size={15} /> Personalized practice plan</p><h2>One focus at a time.</h2><p>Drills are selected from repeated, measurable findings—not generated from a single unusual stroke.</p></div>
          <div className="practice-list">
            {displayPractice.map((item, index) => (
              <article key={`${item.title}-${index}`}>
                <span>{String(index + 1).padStart(2, "0")}</span><div><h3>{item.title}</h3><p><strong>Cue</strong>{item.cue}</p><p><strong>Drill</strong>{item.drill}</p><p><strong>Re-check</strong>{item.check}</p></div>
                <button type="button" onClick={() => isMeasured && seekTo(item.seekTime)} disabled={!isMeasured} aria-label={`Watch evidence for ${item.title}`}><Play size={17} /></button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="progress-section" id="progress">
        <SectionHeading eyebrow="Private progress" title="Compare like with like" measured={isMeasured} />
        <div className="progress-layout">
          <article className="current-session-card">
            <div><TrendingUp size={22} /><span><p>Current report</p><strong>{displayMetrics.overallScore ?? "—"}<small>{displayMetrics.overallScore === null ? "" : "/100"}</small></strong></span></div>
            <p>Progress comparisons match discipline, boat class, camera view, and a similar stroke rate. Videos are never saved.</p>
            {isMeasured ? (
              <button type="button" onClick={saveReport} disabled={savedCurrent}><Save size={17} />{savedCurrent ? "Saved on this device" : "Save report on this device"}</button>
            ) : <span className="progress-hint">Analyze a clip to create your first private baseline.</span>}
          </article>
          <article className="comparison-card">
            <p>Matched comparison</p>
            {isMeasured && matchedPrevious ? (
              <>
                <strong>{displayMetrics.overallScore !== null && matchedPrevious.overallScore !== null ? `${displayMetrics.overallScore - matchedPrevious.overallScore >= 0 ? "+" : ""}${displayMetrics.overallScore - matchedPrevious.overallScore}` : "Rhythm only"}</strong>
                <span>versus {formatDate(matchedPrevious.savedAt)} · {matchedPrevious.strokeRate ?? "—"} spm</span>
                <small>Only treat a change as meaningful when confidence is similar and the pattern repeats.</small>
              </>
            ) : <><strong>New baseline</strong><span>No closely matched earlier report yet.</span><small>Record again from the same fixed position and a similar rate.</small></>}
          </article>
          <article className="history-card">
            <div><p>Recent reports</p><span>Stored locally</span></div>
            {history.length ? history.slice(0, 5).map((session) => (
              <div className="history-row" key={session.id}><span>{formatDate(session.savedAt)}<small>{session.boatClass} · {session.strokeRate ?? "—"} spm</small></span><i><b style={{ width: `${session.overallScore ?? session.confidence}%` }} /></i><strong>{session.overallScore ?? "—"}</strong></div>
            )) : <p className="empty-history">No saved reports yet. Your derived results can stay private in this browser.</p>}
          </article>
        </div>
      </section>

      <section className="method-section" id="method">
        <div><p className="eyebrow"><Video size={15} /> Built for real rowing footage</p><h2>Accuracy before a score.</h2><p>StrokeScore tracks the most visible side, smooths multiple movement signals, detects complete cycles, and only reports a technique finding when it repeats with sufficient confidence.</p></div>
        <ol>
          <li><span>01</span><div><strong>Record side-on</strong><p>Use 8–90 seconds, a fixed camera, and keep the complete athlete visible.</p></div></li>
          <li><span>02</span><div><strong>Verify the movement</strong><p>Landmark visibility, frame coverage, periodicity, and cycle quality are checked independently.</p></div></li>
          <li><span>03</span><div><strong>Inspect the evidence</strong><p>Every score links back to a phase, measurement, or replayable moment.</p></div></li>
        </ol>
        <div className="method-note"><ShieldCheck size={20} /><p><strong>Privacy first.</strong> Analysis and progress storage stay in your browser. Results are two-dimensional coaching estimates, not medical assessment, force measurement, or laboratory motion capture.</p></div>
      </section>

      <footer><a className="brand compact" href="#analyze"><span className="brand-mark"><Waves size={19} /></span><span>StrokeScore <em>AI</em></span></a><p>Markerless rowing analysis · Prototype v2</p></footer>
    </main>
  );
}

function SectionHeading({ eyebrow, title, measured }: { eyebrow: string; title: string; measured: boolean }) {
  return <div className="section-heading"><div><p className="eyebrow"><Activity size={15} />{eyebrow}</p><h2>{title}</h2></div><span className={`data-badge ${measured ? "measured" : "sample"}`}>{measured ? <Check size={15} /> : <Info size={15} />}{measured ? "Uploaded-video estimates" : "Sample output"}</span></div>;
}

function MetricCard({ icon, label, value, unit, accent, detail }: { icon: React.ReactNode; label: string; value: string; unit?: string; accent: "cyan" | "green" | "amber" | "red"; detail: string }) {
  return <article className={`metric-card ${accent}`} tabIndex={0}><span className="metric-icon">{icon}</span><div className="metric-copy"><p>{label}</p><strong>{value} {unit && <small>{unit}</small>}</strong><span className="metric-detail">{detail}</span></div><Info className="info-icon" size={15} aria-hidden="true" /></article>;
}

function Measurement({ label, value, note }: { label: string; value: string; note: string }) {
  return <article className="measurement-card"><p>{label}</p><strong>{value}</strong><span>{note}</span></article>;
}
